/**
 * Tedarikçi kartında şirket profili ve banka hesapları (ADR 0114 §6): API biçimleri. Profil
 * tedarikçiden gelir, ofiste salt okunur; hesap numarası maskelidir.
 */

export interface ProfileAddress {
  line1: string;
  line2: string | null;
  city: string;
  postalCode: string | null;
  region: string | null;
  country: string;
}

export interface ProfileContact {
  name: string;
  role: string;
  email: string;
  phone: string | null;
}

export interface SupplierCompanyDetails {
  legalName: string;
  taxId: string;
  taxOffice: string | null;
  taxCountry: string;
  billingAddress: ProfileAddress;
  shippingAddress: ProfileAddress | null;
  contacts: ProfileContact[];
}

export interface BankVersionView {
  versionId: string;
  versionNo: number;
  change: 'added' | 'changed' | 'default' | 'removed';
  detailsVersionId: string;
  holderName: string;
  bankName: string;
  kind: 'iban' | 'account';
  country: string | null;
  masked: string;
  swiftBic: string | null;
  currency: string;
  isDefault: boolean;
  removed: boolean;
  createdAt: string;
}

export type BankReviewAction = 'verified' | 'approved' | 'rejected';
export type BankStatus = 'pending' | BankReviewAction | 'removed';

export interface BankReviewView {
  id: string;
  versionId: string;
  versionNo: number | null;
  action: BankReviewAction;
  note: string | null;
  userId: string;
  userName: string | null;
  at: string;
}

export interface SupplierBankAccountView {
  bankAccountId: string;
  current: BankVersionView;
  status: BankStatus;
  reviewVersionId: string;
  detailsHash: string;
  /** Ödemede geçerli: onaylı son ayrıntı; yoksa `null`. */
  effective: BankVersionView | null;
  canApprove: boolean;
  reviews: BankReviewView[];
  versions: BankVersionView[];
}

export interface SupplierProfileView {
  portal: {
    status: 'invited' | 'active' | 'prospect';
    accountName: string | null;
    accountEmail: string | null;
    /** Platformun belge doğrulaması ve askı (ADR 0120); eski API'de yok. */
    verified?: boolean;
    suspended?: boolean;
  } | null;
  details: SupplierCompanyDetails | null;
  updatedAt: string | null;
  /** Yalnız etkin bağda (şirket tedarikçisi); aday tedarikçide `null`. */
  bank: { accounts: SupplierBankAccountView[] } | null;
  can: { review: boolean };
}

export interface RevealedBankNumber {
  iban: string | null;
  accountNumber: string | null;
  swiftBic: string | null;
}

/** Zil: onay bekleyen banka değişiklikleri (ADR 0114 §5). */
export interface PendingBankChange {
  supplierId: string;
  supplierName: string;
  bankAccountId: string;
  versionId: string;
  status: 'pending' | 'verified';
  bankName: string;
  masked: string;
  currency: string;
  at: string;
  /** Kişi doğruladı; başkasının onayı bekleniyor. */
  awaitingOther: boolean;
}

/** Partner yorumu (ADR 0120 §4): yayındakiler yazan şirketsiz; kendi yorumları durumuyla. */
export type SupplierReviewStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

export interface SupplierReviewsView {
  portal: boolean;
  verified: boolean;
  summary: { count: number; average: number | null } | null;
  items: Array<{ rating: number; comment: string; month: string; own: boolean }>;
  mine: Array<{
    id: string;
    poNo: string | null;
    rating: number;
    comment: string;
    createdAt: string;
    status: SupplierReviewStatus;
    reason: string | null;
  }>;
  /** Yorum yazılabilecek teslim alınmış siparişler; `rewrite`: reddedilenin yerine. */
  orders: Array<{ id: string; poNo: string; rewrite: boolean }>;
  canWrite: boolean;
}

export interface SupplierProfileSource {
  get(supplierId: string): Promise<SupplierProfileView>;
  /** Partner yorumları (ADR 0120 §4); yoksa sekme çizilmez. */
  reviews?(supplierId: string): Promise<SupplierReviewsView>;
  writeReview?(
    supplierId: string,
    input: { purchaseOrderId: string; rating: number; comment: string },
  ): Promise<SupplierReviewsView>;
  reveal(supplierId: string, versionId: string): Promise<RevealedBankNumber>;
  review(
    supplierId: string,
    versionId: string,
    input: { action: BankReviewAction; note?: string; detailsHash: string; password?: string },
  ): Promise<SupplierProfileView>;
}

/** Tedarikçiler sayfasında kartın profilini açan yol (zil bağlantısı). */
export function supplierProfilePath(supplierId: string): string {
  return `/purchasing/suppliers?profile=${encodeURIComponent(supplierId)}`;
}
